import React, { useState, useMemo, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Check, ChevronDown, UserPlus, Search, X as XIcon } from 'lucide-react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { cn } from '@/lib/utils';
import { ClientQuickCreate } from './ClientQuickCreate';

/**
 * ClientPicker — searchable client select with inline "+ Client Baru" CTA.
 * Props:
 *   value: string (client_id)
 *   onChange: (id) => void
 *   clients: array of client objects {id, client_name, company_name}
 *   onClientsRefresh: optional async callback to refresh client list after create
 *   testid: prefix for data-testid attrs
 */
export const ClientPicker = ({
  value, onChange,
  clients = [], onClientsRefresh,
  testid = 'client-picker',
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [quickOpen, setQuickOpen] = useState(false);
  // Locally cache the freshly-created client so the trigger label is correct
  // even before the parent's clients prop is refreshed.
  const [pendingClient, setPendingClient] = useState(null);

  const mergedClients = useMemo(() => {
    if (!pendingClient) return clients;
    if (clients.some(c => c.id === pendingClient.id)) return clients;
    return [pendingClient, ...clients];
  }, [clients, pendingClient]);

  // Clear the pending cache once the official client list catches up
  useEffect(() => {
    if (pendingClient && clients.some(c => c.id === pendingClient.id)) {
      setPendingClient(null);
    }
  }, [clients, pendingClient]);

  const selected = useMemo(
    () => mergedClients.find(c => c.id === value),
    [mergedClients, value]
  );
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return mergedClients;
    return mergedClients.filter(c =>
      (c.client_name || '').toLowerCase().includes(q) ||
      (c.company_name || '').toLowerCase().includes(q) ||
      (c.pic_name || '').toLowerCase().includes(q)
    );
  }, [mergedClients, search]);

  const handleCreated = async (newClient) => {
    // Optimistically cache, select, then refresh in background
    setPendingClient(newClient);
    onChange && onChange(newClient.id);
    setOpen(false);
    setSearch('');
    if (onClientsRefresh) {
      try { await onClientsRefresh(); } catch (_e) { /* noop */ }
    }
  };

  return (
    <>
      <div className="flex items-stretch gap-2">
        <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
          <PopoverPrimitive.Trigger asChild>
            <button
              type="button"
              disabled={disabled}
              className={cn(
                'flex-1 inline-flex items-center justify-between gap-2 rounded-md border border-input bg-background px-3',
                'h-11 md:h-9 text-base md:text-sm text-left',
                'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                'disabled:opacity-60 disabled:cursor-not-allowed'
              )}
              data-testid={`${testid}-trigger`}
            >
              <span className={cn('truncate', selected ? 'text-foreground' : 'text-muted-foreground')}>
                {selected ? (
                  <>
                    {selected.client_name}
                    {selected.company_name && <span className="ml-1.5 text-xs text-muted-foreground">• {selected.company_name}</span>}
                  </>
                ) : 'Pilih client'}
              </span>
              <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
            </button>
          </PopoverPrimitive.Trigger>
          <PopoverPrimitive.Portal>
            <PopoverPrimitive.Content
              align="start"
              sideOffset={4}
              className="z-[55] w-[var(--radix-popover-trigger-width)] min-w-[260px] rounded-md border border-border bg-card shadow-xl overflow-hidden"
            >
              <div className="flex items-center gap-2 border-b border-border px-3 py-2">
                <Search className="h-4 w-4 text-muted-foreground shrink-0" />
                <Input
                  autoFocus
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Cari nama / perusahaan / PIC…"
                  className="h-8 border-0 px-0 bg-transparent focus-visible:ring-0 text-sm"
                  data-testid={`${testid}-search`}
                />
                {search && (
                  <button type="button" onClick={() => setSearch('')} className="text-muted-foreground hover:text-foreground">
                    <XIcon className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <div className="max-h-[260px] overflow-y-auto py-1" data-testid={`${testid}-list`}>
                {filtered.length === 0 ? (
                  <div className="px-3 py-4 text-center space-y-2">
                    <p className="text-xs text-muted-foreground">
                      {search ? 'Client tidak ditemukan.' : 'Belum ada client.'}
                    </p>
                    <Button
                      type="button" size="sm" className="w-full"
                      onClick={() => { setOpen(false); setQuickOpen(true); }}
                      data-testid={`${testid}-add-from-empty`}
                    >
                      <UserPlus className="h-3.5 w-3.5 mr-1.5" /> Tambah Client Baru
                    </Button>
                  </div>
                ) : (
                  filtered.map(c => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => { onChange && onChange(c.id); setOpen(false); setSearch(''); }}
                      className={cn(
                        'w-full text-left px-3 py-2 text-sm hover:bg-white/5 flex items-start gap-2',
                        c.id === value ? 'bg-primary/10' : ''
                      )}
                      data-testid={`${testid}-option-${c.id}`}
                    >
                      <Check className={cn('h-4 w-4 mt-0.5 shrink-0', c.id === value ? 'text-primary' : 'opacity-0')} />
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate text-slate-100">{c.client_name}</div>
                        {(c.company_name || c.pic_name) && (
                          <div className="text-xs text-muted-foreground truncate">
                            {[c.company_name, c.pic_name].filter(Boolean).join(' • ')}
                          </div>
                        )}
                      </div>
                    </button>
                  ))
                )}
              </div>
            </PopoverPrimitive.Content>
          </PopoverPrimitive.Portal>
        </PopoverPrimitive.Root>
        <Button
          type="button"
          variant="secondary"
          className="h-11 md:h-9 shrink-0 whitespace-nowrap"
          onClick={() => setQuickOpen(true)}
          disabled={disabled}
          data-testid={`${testid}-add-btn`}
        >
          <UserPlus className="h-4 w-4 sm:mr-1.5" />
          <span className="hidden sm:inline">Client Baru</span>
        </Button>
      </div>
      <ClientQuickCreate
        open={quickOpen}
        onOpenChange={setQuickOpen}
        onCreated={handleCreated}
        existingClients={clients}
      />
    </>
  );
};
